import { Button } from '@heroui/react'
import { useCreateReseller, useUpdateReseller } from '@qse/api'
import { addResellerValidationSchema, type ModalComponentProps } from '@qse/constants'
import { FormikControl } from '@qse/ui'
import { Form, Formik } from 'formik'
import { toast } from 'sonner'

interface AddResellerFormValues {
  name: string
  description: string
  maxTenants: number | null
  contactEmail: string
  contactPerson: string
  phoneNumber: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  zipcode: string
  countryCode: string
  storagePricePerGbCents: number
  apiCallPriceCents: number
}

export default function AddReseller(props: ModalComponentProps) {
  const { setModelActive, onClose, data } = props

  const { mutateAsync, isPending: isCreating } = useCreateReseller()
  const { mutateAsync: mutateAsyncUpdateReseller, isPending: isUpdating } = useUpdateReseller()

  const isPending = isCreating || isUpdating

  // Helper function to get field value based on action
  const getFieldValue = (fieldName: keyof AddResellerFormValues, defaultValue: any) => {
    return data?.action === 'edit' ? data.data[fieldName] : defaultValue
  }

  const onSubmit = async (
    values: AddResellerFormValues,
    { resetForm }: { resetForm: () => void },
  ) => {
    console.log(values)
    // Normalize maxTenants: empty string or 0 from a cleared number input → null (unlimited)
    const submitValues = {
      ...values,
      maxTenants:
        values.maxTenants === ('' as any) ||
        values.maxTenants === null ||
        values.maxTenants === undefined
          ? null
          : Number(values.maxTenants),
    }

    try {
      if (data?.action === 'edit') {
        await mutateAsyncUpdateReseller({ id: data.data.id, data: submitValues })
        toast.success('Reseller updated successfully!')
      } else {
        await mutateAsync(submitValues)
        toast.success('Reseller created successfully!')
      }

      resetForm()
      setModelActive(undefined)
      onClose()
    } catch (error) {
      toast.error(data?.action === 'edit' ? 'Error updating reseller!' : 'Error creating reseller!')
    }
  }

  const handleCancel = () => {
    setModelActive(undefined)
    onClose()
  }

  const initialValues: AddResellerFormValues = {
    name: getFieldValue('name', 'Acme Corp'),
    description: getFieldValue('description', 'Leading technology solutions provider'),
    maxTenants: getFieldValue('maxTenants', null),
    contactEmail: getFieldValue('contactEmail', 'contact@acmecorp.com'),
    contactPerson: getFieldValue('contactPerson', 'John Doe'),
    phoneNumber: getFieldValue('phoneNumber', '+1-555-123-4567'),
    addressLine1: getFieldValue('addressLine1', '123 Main Street'),
    addressLine2: getFieldValue('addressLine2', 'Suite 100'),
    city: getFieldValue('city', 'New York'),
    state: getFieldValue('state', 'NY'),
    zipcode: getFieldValue('zipcode', '10001'),
    countryCode: getFieldValue('countryCode', 'US'),
    storagePricePerGbCents: getFieldValue('storagePricePerGbCents', 1000),
    apiCallPriceCents: getFieldValue('apiCallPriceCents', 10),
  }

  return (
    <div>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={false}
        validationSchema={addResellerValidationSchema}
        onSubmit={onSubmit}
      >
        {({ handleSubmit }) => (
          <Form className="flex flex-col gap-5 py-5 " onSubmit={handleSubmit}>
            <div className="">
              <div className="grid grid-cols-2 gap-5">
                <h1 className="text-xl font-medium">Reseller Information</h1>
                <div className="col-span-full">
                  <FormikControl
                    isRequired
                    control="text"
                    label="Company Name"
                    name="name"
                    placeholder="Enter company name"
                  />
                </div>
                <div className="col-span-full">
                  <FormikControl
                    isRequired
                    control="textarea"
                    label="Description"
                    name="description"
                    placeholder="Enter company description"
                  />
                </div>
                <h1 className="text-xl font-medium">Primary Contact</h1>

                <div className="col-span-full">
                  <FormikControl
                    isRequired
                    control="text"
                    label="Contact Email"
                    name="contactEmail"
                    placeholder="Enter contact email"
                  />
                </div>
                <div className="col-span-full">
                  <FormikControl
                    isRequired
                    control="text"
                    label="Contact Person"
                    name="contactPerson"
                    placeholder="Enter contact person name"
                  />
                </div>
                <div className="col-span-full">
                  <FormikControl
                    isRequired
                    control="text"
                    label="Phone Number"
                    name="phoneNumber"
                    placeholder="Enter phone number"
                  />
                </div>

                <h1 className="text-xl font-medium">Company Address</h1>

                <div className="col-span-full flex flex-col gap-5">
                  <FormikControl
                    isRequired
                    control="text"
                    label="Address Line 1"
                    name="addressLine1"
                    placeholder="Enter address line 1"
                  />
                </div>

                <div className="col-span-full">
                  <FormikControl
                    control="text"
                    label="Address Line 2"
                    name="addressLine2"
                    placeholder="Enter address line 2 (optional)"
                  />
                </div>

                <FormikControl
                  isRequired
                  control="text"
                  label="Country Code"
                  name="countryCode"
                  placeholder="Enter country code (e.g., US)"
                />

                <FormikControl
                  isRequired
                  control="text"
                  label="Zip Code"
                  name="zipcode"
                  placeholder="Enter zip code"
                />

                <FormikControl
                  isRequired
                  control="text"
                  label="City"
                  name="city"
                  placeholder="Enter city"
                />

                <FormikControl
                  isRequired
                  control="text"
                  label="State"
                  name="state"
                  placeholder="Enter state"
                />

                <h1 className="text-xl font-medium">Pricing & Limits</h1>

                <div className="col-span-full">
                  <FormikControl
                    isRequired
                    control="number"
                    label="Storage Price per GB (cents)"
                    name="storagePricePerGbCents"
                    placeholder="Enter price per GB in cents"
                  />
                </div>

                <div className="col-span-full">
                  <FormikControl
                    isRequired
                    control="number"
                    label="API Call Price (cents)"
                    name="apiCallPriceCents"
                    placeholder="Enter price per API call in cents"
                  />
                </div>

                <div className="col-span-full">
                  <FormikControl
                    control="number"
                    label="Maximum Number of Tenants"
                    name="maxTenants"
                    placeholder="Leave empty for unlimited"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button disabled={isPending} radius="sm" variant="flat" onPress={handleCancel}>
                Cancel
              </Button>
              <Button color="primary" isLoading={isPending} radius="sm" type="submit">
                {data?.action === 'edit' ? 'Update Reseller' : 'Add Reseller'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}
